import { useState, useRef, useEffect } from "react";
import {
  MessageCircle,
  Send,
  Loader,
  Bot,
  User,
  Sparkles,
  Trash2,
  ChevronRight,
  BotIcon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

export const getGroqClient = () => {
  const key = import.meta.env.VITE_GROQ_API_KEY;
  if (!key || typeof key !== "string" || !key.startsWith("gsk_")) {
    return null;
  }
  return { key };
};

export const suggestions = [
  "📚 How do I enroll in a course?",
  "👨‍🏫 How do I find a mentor?",
  "🎓 How do I get a certificate?",
  "📖 Where are my enrolled courses?",
  "🤝 How does mentorship work?",
  "🏷️ How do I use a coupon?",
  "💡 What courses are best for beginners?",
  "⭐ How do I leave a review?",
];

export const pathButtonMap: Record<
  string,
  { label: string; icon: string; description?: string; href?: string }
> = {
  "/mentorship/": { label: "Mentors", icon: "👨‍🏫", description: "View all mentors details" },
  "/dashboard/student/courses": { label: "Continue Learning", icon: "▶️", description: "Resume your course here" },
  "/mentorship": { label: "Find Mentors", icon: "👨‍🏫", description: "Connect with expert mentors" },
  "/forums": { label: "Community Forums", icon: "💬", description: "Join discussions" },
  "/dashboard/role": { label: "Dashboard", icon: "🏠", description: "Go to your dashboard" },
  "/dashboard/student": { label: "Student Dashboard", icon: "🎓", description: "View your learning progress" },
  "/dashboard/instructor": { label: "Instructor Dashboard", icon: "👨‍🏫", description: "Manage your courses" },
  "/dashboard/mentor": { label: "Mentor Dashboard", icon: "🧑‍🏫", description: "Manage mentorship sessions" },
  "/profile": { label: "My Profile", icon: "👤", description: "View your profile" },
  "/settings": { label: "Settings", icon: "⚙️", description: "Account settings" },
  "/dashboard/student/certificates": { label: "My Certificates", icon: "🏆", description: "View earned certificates" },
  "/dashboard/wishlist": { label: "My Wishlist", icon: "❤️", description: "Saved courses" },
  "/reviews": { label: "My Reviews", icon: "⭐", description: "Your course reviews" },
  "/dashboard/student/quizzes": { label: "Quizzes", icon: "❓", description: "Take quizzes" },
  "/dashboard/student/badges": { label: "Badges & Achievements", icon: "🎖️", description: "Your accomplishments" },
  "/dashboard/notifications": { label: "Notifications", icon: "🔔", description: "View notifications" },
  "/dashboard/student/transactions": { label: "Payment History", icon: "💳", description: "View transactions" },
  "/contact": { label: "Contact Us", icon: "📧", description: "Get in touch with our support team" },
  "/careers": { label: "Careers", icon: "💼", description: "Explore career opportunities, apply for mentor or instructor Role" },
};

export function parseMessageWithButtons(text: string): { text: string; paths: string[] } {
  const pathRegex = /\/[-\w/:]+/g;
  const paths = text.match(pathRegex) || [];
  return { text, paths: [...new Set(paths)] };
}

export function getNormalizedPath(path: string): string {
  return path.replace(/:\w+/g, "").replace(/\/+$/, "");
}

export function getPathInfo(path: string): { label: string; icon: string; description?: string } | null {
  if (pathButtonMap[path]) return pathButtonMap[path];
  const normalized = getNormalizedPath(path);
  if (pathButtonMap[normalized]) return pathButtonMap[normalized];
  for (const [pattern, info] of Object.entries(pathButtonMap)) {
    if (pattern.includes(":")) {
      const patternRegex = new RegExp("^" + pattern.replace(/:\w+/g, "[^/]+") + "$");
      if (patternRegex.test(path) || patternRegex.test(normalized)) return info;
    }
  }
  const parts = normalized.split("/").filter(Boolean);
  if (parts.length > 0) {
    const pageName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    for (const [mapPath, info] of Object.entries(pathButtonMap)) {
      if (normalized.startsWith(mapPath.replace(/:\w+/g, ""))) {
        return info;
      }
    }
    return { label: pageName, icon: "🔗" };
  }
  return null;
}

const getInitialMessages = (): Message[] => [
  {
    id: "welcome",
    role: "assistant",
    content: "👋 Hi! I'm your LearnHub AI assistant. I can help you with courses, mentorship, certificates, and more! What would you like to know?",
    timestamp: new Date(),
  },
];

const systemPrompt = `You are a helpful AI learning assistant for LearnHub - a full-featured online education platform.

IMPORTANT INSTRUCTION ABOUT LINKS:
When responding, include specific page references using paths like:
- "/courses" for the courses catalog
- "/mentorship" for finding mentors
- "/forums" for community discussions
- "/dashboard/student" for the student dashboard
- "/dashboard/instructor" for instructor tools
- "/dashboard/mentor" for mentor dashboard
- "/profile" for user profile
- "/dashboard/settings" for account settings
- "/dashboard/student/certificates" for certificates
- "/dashboard/wishlist" for saved courses
- "/dashboard/student/quizzes" for quizzes
- "/dashboard/student/badges" for achievements
- "/dashboard/notifications" for notifications
- "/dashboard/student/transactions" for payment history
- "/contact" for contacting support
- "/careers" for career opportunities, apply for mentor or instructor Role
- "/dashboard/student/sessions" to view all your mentorship sessions
- "/dashboard/student/schedule" to view your calendar and see the days with sessions
When you mention these paths, the interface will automatically convert them into clickable buttons.
For example, instead of saying "You can find courses on the courses page", say "You can find all courses on /courses".

PLATFORM OVERVIEW:
- Users: Students, Instructors, Mentors, Admins
- Courses: Video lessons, quizzes, assignments, modules, certificates
- Mentorship: 1-on-1 sessions with mentors
- Forums: Community discussions with categories
- Gamification: Points, badges, achievements
- Leaderboard: Leaderboard for top learners base on points and achievement
- Coupons: Discount codes for courses

COURSE STRUCTURE:
- Course → Modules → Lessons
- Each course has: title, description, category, difficulty (beginner/intermediate/advanced), price, language
- Content: video lessons, text content, attachments
- Each Module has a Quiz with questions
- Enrollment: free or paid courses
- Progress: tracked per lesson, per Module and per course
- Certificate: issued for courses with Certificate, after completing course + exam

MENTORSHIP:
- Mentor profiles with expertise, bio, hourly rate
- Availability schedules
- Session bookings
- Reviews/ratings after sessions

FORUMS:
- Forum categories
- Discussions (posts)
- Replies/comments

KEY PAGES (reference these in your responses):
- /courses - browse all courses
- /dashboard/student/courses - learning interface
- /mentorship - find mentors
- /forums - community forums
- /dashboard/student/courses - enrolled courses, badges. must be login first
- /dashboard/instructor - create/manage courses
- /dashboard/mentor - manage mentorship sessions

COMMON TASKS:
- Enroll: Go to course detail page → click Enroll
- Start learning: Go to /dashboard/student/courses
- Find mentor: Browse /mentorship
- Get certificate: Complete all lessons + pass exam
- Use coupon: Enter code at checkout
- View profile: /profile
- Account settings: /settings
- View certificates: /dashboard/student/certificates
- To contact Us: /contact


Be friendly, helpful, and concise. Use emojis occasionally. Keep responses under 200 words. ALWAYS use the provided paths when directing users to specific pages.

If a user asks a general learning or study question that is outside the platform features above, still answer helpfully from general knowledge — for example, explaining concepts, study tips, how to approach a topic, motivation, or career advice.`;

interface AIChatInterfaceProps {
  isFullPage?: boolean;
}

const AIChatInterface = ({ isFullPage = false }: AIChatInterfaceProps) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>(() => {
    if (!isFullPage) {
      return [];
    }
    return getInitialMessages();
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const userAvatar = user?.avatar || null;

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const groq = getGroqClient();
    if (!groq) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          role: "assistant",
          content: "⚠️ AI Tutor is not configured. Please add VITE_GROQ_API_KEY to your .env file.",
          timestamp: new Date(),
        },
      ]);
      setLoading(false);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const conversationMessages = messages
        .filter((m) => m.id !== "welcome")
        .map((m) => ({
          role: m.role as "user" | "assistant",
          content: m.content,
        }));

      conversationMessages.push({
        role: "user" as const,
        content: userMessage.content,
      });

      const response = await fetch(
        "https://api.groq.com/openai/v1/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${groq.key}`,
          },
          body: JSON.stringify({
            model: "llama-3.1-8b-instant",
            messages: [
              { role: "system", content: systemPrompt },
              ...conversationMessages,
            ],
            max_tokens: 600,
            temperature: 0.7,
          }),
        },
      );

      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || `API Error: ${response.status}`);
      }

      const data = await response.json();
      const aiResponse =
        data.choices?.[0]?.message?.content ||
        "I couldn't get a response. Please try again.";

      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: aiResponse,
          timestamp: new Date(),
        },
      ]);
    } catch (error: any) {
      console.error("AI Error:", error);
      let errorMsg = "Sorry, something went wrong. Please try again.";
      if (
        error?.message?.includes("quota") ||
        error?.message?.includes("exceeded")
      ) {
        errorMsg = "⏳ AI service quota exceeded. Please try again later.";
      } else if (error?.message?.includes("Invalid API Key")) {
        errorMsg = "🔑 Invalid API key. Please check VITE_GROQ_API_KEY in .env";
      }
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: errorMsg,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages(getInitialMessages());
    setShowSuggestions(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div
        className="flex-1 p-4 overflow-y-auto bg-gradient-to-b from-orange-50 to-white"
        style={isFullPage ? { maxHeight: "calc(100vh - 180px)", paddingBottom: "7rem" } : { maxHeight: "400px", minHeight: "300px" }}
      >
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex mb-3 ${msg.role === "user" ? "justify-end" : "justify-start"} animate-fade-in`}
          >
            <div
              className={`flex max-w-[90%] gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 overflow-hidden ${
                  msg.role === "user"
                    ? "bg-primary"
                    : "bg-gradient-to-r from-primary to-orange-500"
                }`}
              >
                {msg.role === "user" ? (
                  userAvatar ? (
                    <img
                      src={userAvatar}
                      alt="Profile"
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <User className="w-3.5 h-3.5 text-white" />
                  )
                ) : (
                  <Bot className="w-3.5 h-3.5 text-white" />
                )}
              </div>

              <div
                className={`px-3 py-2 rounded-xl text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-primary text-white rounded-br-md"
                    : "bg-orange-50 border border-orange-100 text-gray-800 rounded-bl-md shadow-sm"
                }`}
              >
                <div className="break-words whitespace-pre-wrap text-[13px]">
                  {msg.content}
                </div>

                {msg.role === "assistant" && (() => {
                  const { paths } = parseMessageWithButtons(msg.content);
                  if (paths.length > 0) {
                    return (
                      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-100">
                        {paths.map((path, idx) => {
                          const pathInfo = getPathInfo(path);
                          if (!pathInfo) return null;
                          const normalizedPath = getNormalizedPath(path);
                          return (
                            <button
                              key={idx}
                              onClick={() => {
                                window.location.href = normalizedPath;
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] transition-all duration-200 bg-primary/5 border border-primary/20 text-primary rounded-full hover:bg-primary/10 hover:border-primary/40"
                              title={pathInfo.description || `Go to ${pathInfo.label}`}
                            >
                              <span>{pathInfo.icon}</span>
                              <span>{pathInfo.label}</span>
                              <ChevronRight className="w-2.5 h-2.5" />
                            </button>
                          );
                        })}
                      </div>
                    );
                  }
                  return null;
                })()}

                {msg.timestamp && (
                  <p
                    className={`text-[9px] mt-1 ${
                      msg.role === "user" ? "text-white/60" : "text-gray-400"
                    }`}
                  >
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start mb-3 animate-fade-in">
            <div className="flex gap-2">
              <div className="flex items-center justify-center rounded-full w-7 h-7 bg-gradient-to-r from-primary to-orange-500">
                <Bot className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="px-3 py-2 bg-white border border-gray-200 shadow-sm rounded-xl rounded-bl-md">
                <div className="flex gap-1">
                  <div
                    className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <div
                    className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <div
                    className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {showSuggestions && !loading && messages.length <= 2 && (
          <div className="mb-3">
            <p className="text-[11px] text-gray-500 mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-primary" />
              Suggested questions:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.slice(0, 6).map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setInput(suggestion);
                    setShowSuggestions(false);
                  }}
                  className="px-2.5 py-1.5 text-[11px] transition-all duration-200 bg-white border shadow-sm border-primary/20 text-primary rounded-full hover:bg-primary/5 hover:border-primary/40"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ==================== REDESIGNED SUBMIT FORM ==================== */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (showSuggestions) setShowSuggestions(false);
          handleSend();
        }}
        className="p-3 bg-white border-t border-gray-200"
      >
        <div className="flex items-end max-w-4xl gap-2 mx-auto">
          <div className="relative flex-1">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask me anything..."
              disabled={loading}
              rows={1}
              className="w-full px-4 py-2.5 text-sm text-gray-800 bg-gray-100 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:bg-white focus:border-orange-300 transition-all resize-none placeholder:text-gray-400"
              style={{ minHeight: "48px", maxHeight: "120px" }}
            />
            {/* Character count / hint */}
            <div className="absolute right-3 bottom-2 text-[9px] text-gray-400 pointer-events-none">
              {input.length > 0 && <span>{input.length}</span>}
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="flex items-center justify-center w-12 h-12 text-white transition-all duration-200 shadow-md rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed shrink-0"
          >
            {loading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        
        {/* Clear chat link */}
        {messages.length > 1 && (
          <div className="flex justify-center mt-2">
            <button
              type="button"
              onClick={handleClearChat}
              className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              Clear conversation
            </button>
          </div>
        )}
      </form>
      {/* ==================== END REDESIGNED SUBMIT FORM ==================== */}

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
        .animate-fade-in {
          animation: fade-in 0.25s ease-out;
        }
        .animate-bounce {
          animation: bounce 1.4s infinite;
        }
      `}</style>
    </div>
  );
};

export default AIChatInterface;